import React, { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { LoadingSpinner } from './ui';

export const ProtectedRoute: React.FC = () => {
  const { isAuthenticated, isLoading, login } = useAuth();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      login();
    }
  }, [isLoading, isAuthenticated, login]);

  if (isLoading || !isAuthenticated) {
    return <LoadingSpinner />;
  }

  return <Outlet />;
};

// Kept for backward compat — renders children inline based on auth/role state
interface ConditionalRenderProps {
  children: React.ReactNode;
  roles?: string[];
  requireAllRoles?: boolean;
  fallback?: React.ReactNode;
  requireAuth?: boolean;
}

export const ConditionalRender: React.FC<ConditionalRenderProps> = ({
  children,
  roles = [],
  requireAllRoles = false,
  fallback = null,
  requireAuth = true,
}) => {
  const { isAuthenticated, hasAnyRole, hasAllRoles } = useAuth();

  if (requireAuth && !isAuthenticated) return <>{fallback}</>;
  if (roles.length === 0) return isAuthenticated || !requireAuth ? <>{children}</> : <>{fallback}</>;

  const ok = requireAllRoles ? hasAllRoles(roles) : hasAnyRole(roles);
  return ok ? <>{children}</> : <>{fallback}</>;
};
